import type { QueryClient } from '@tanstack/react-query';

import type { DecompositionNode } from 'api/data/projects/types';
import { normalizeId } from 'utils/ids';

import type { TreeCacheScope } from '../types';
import { treeQueryKeys } from './treeQueryKeys';

/**
 * Scope-level concerns that are not key construction — that lives in `treeQueryKeys.ts`, which owns
 * the key layout and normalizes ids for every key it builds.
 */

/**
 * An absent proposal is `undefined`, never `''` or `null`, so "live" is one value however it
 * arrived. A present one is normalized: it gets compared against the proposal id parsed from the
 * route, and a SignalR payload can disagree with the URL on casing.
 */
export function normalizeTreeProposalId(proposalId?: string | null): string | undefined {
  if (!proposalId) return undefined;
  return normalizeId(proposalId);
}

/**
 * `normalId` must already be normalized: callers scanning many nodes normalize the needle once,
 * outside the loop.
 */
export function nodeMatchesId(node: DecompositionNode, normalId: string): boolean {
  return normalizeId(node.id) === normalId;
}

/** Fires `onStoreChange` whenever any query in this tree scope is added, updated or removed. */
export function subscribeToTreeCacheScope(
  queryClient: QueryClient,
  scope: TreeCacheScope,
  onStoreChange: () => void
): () => void {
  const inScope = treeQueryKeys.inScope(scope);
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' && event.type !== 'added' && event.type !== 'removed') return;
    if (inScope(event.query)) {
      queueMicrotask(onStoreChange);
    }
  });
}
